import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { SessionProvider } from '@/shared/auth/SessionProvider';
import { PlanProvider } from '@/shared/billing';
// Deep import, not the barrel: `@/shared/components/Grid` re-exports the
// whole grid, and importing it eagerly put the whole 3,800-line grid
// into the initial bundle of EVERY page.
import { GridConfigProvider } from '@/shared/components/Grid/GridConfigProvider';
import { OrgProvider } from '@/shared/org';
import { BrandProvider } from '@/shared/brand/BrandProvider';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import { I18nProvider } from '@/shared/i18n/I18nProvider';
import { ThemeProvider } from '@/shared/theme/ThemeProvider';
import { MotionProvider } from '@/shared/theme/MotionProvider';
// Deep import for the same reason as the grid above: the theme barrel pulls in
// the presets and role table, which only the settings screen needs.
import { ThemeColorsProvider } from '@/shared/theme/ThemeColorsProvider';
import { installStaleBuildRecovery } from '@/shared/pwa/staleBuildRecovery';
import { installSystemTextScale } from '@/shared/theme/systemTextScale';
import './styles/index.css';

installSystemTextScale();

// iOS Safari ignores user-scalable=no; its proprietary gesture events are the
// only way to stop a page pinch there. Work areas zoom themselves (usePinchZoom).
document.addEventListener('gesturestart', (e) => e.preventDefault());

/**
 * `data-brand`, `data-theme` and `<html lang>` were already set by the inline
 * script in index.html, before this bundle loaded. The providers below read
 * the same sources, so mounting causes no flash and no reflow.
 *
 * Provider order matters: ThemeProvider needs the brand to pick the
 * theme-color value for each brand × theme combination. MotionProvider is
 * the same shape as ThemeProvider (profiles.motion_enabled instead of
 * .theme) and sits right after it for that reason, not because anything
 * below depends on it. I18nProvider sits
 * inside SessionProvider so it can resolve `profiles.locale` before falling
 * back to localStorage and the browser. SessionProvider sits inside
 * ErrorBoundary so a failure to reach Supabase renders the boundary instead
 * of an unmounted tree, and outside BrowserRouter so route guards can read
 * the session.
 *
 * PlanProvider goes under OrgProvider because the plan belongs to the tenant:
 * switching organization has to change the limits on screen with it.
 * ThemeColorsProvider sits there too and for the same reason — the palette is
 * the organization's, so a switch must repaint. It wraps GridConfigProvider so
 * that a document-scoped grid, which shadows the palette while a sheet is open,
 * is unmounted before the organization-wide one is recomputed.
 */
const container = document.getElementById('root');
if (!container) throw new Error('#root not found in index.html');

// Before render: a tab that went stale across a deploy fails on its first lazy
// route, which can happen while the tree is still mounting.
installStaleBuildRecovery();

createRoot(container).render(
  <StrictMode>
    <BrandProvider>
      <ErrorBoundary>
        <SessionProvider>
          <I18nProvider>
            <ThemeProvider>
              <MotionProvider>
                <OrgProvider>
                  <PlanProvider>
                    <ThemeColorsProvider>
                      <GridConfigProvider>
                        <BrowserRouter>
                          <App />
                        </BrowserRouter>
                      </GridConfigProvider>
                    </ThemeColorsProvider>
                  </PlanProvider>
                </OrgProvider>
              </MotionProvider>
            </ThemeProvider>
          </I18nProvider>
        </SessionProvider>
      </ErrorBoundary>
    </BrandProvider>
  </StrictMode>,
);
